/**
 * API client — single chokepoint for every HTTP call to the FastAPI backend.
 *
 * Responsibilities:
 *   - Always send `credentials: 'same-origin'` so the JWT cookie reaches the
 *     backend (per spec §4.3).
 *   - Apply 401 interceptor → redirect to /login (preserving the current
 *     path in `?next=`).
 *   - Apply 409 interceptor → throw a typed `ApiConflictError` callers can
 *     branch on without re-parsing.
 *   - Normalize other non-OK responses to `ApiError`.
 *
 * Per spec §4.8: NO localStorage / sessionStorage. We never cache responses
 * client-side — TanStack Query holds them in memory only.
 */

import { userRecentlyActive } from './user-activity';

/** Must match ADMIN_BACKGROUND_HEADER in backend/deps.py. */
export const ADMIN_BACKGROUND_HEADER = 'X-Admin-Background';

export class ApiError extends Error {
  status: number;
  /** The raw `detail` body. Structured 4xx responses carry remediation data
   *  here (which markets failed, what would qualify) so a route can render a
   *  way forward instead of only the server's sentence. */
  detail?: unknown;
  constructor(message: string, status: number, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

export class ApiConflictError extends ApiError {
  detail: unknown;
  constructor(message: string, detail: unknown) {
    super(message, 409);
    this.name = 'ApiConflictError';
    this.detail = detail;
  }
}

/**
 * Server-error response (HTTP 5xx). Distinct from generic ApiError so
 * the global mutation/query error handlers can surface a different toast
 * ("Something went wrong on our end") instead of leaking a raw exception
 * detail to the user.
 */
export class ApiServerError extends ApiError {
  constructor(message: string, status: number) {
    super(message, status);
    this.name = 'ApiServerError';
  }
}

/**
 * Thrown when the fetch itself fails (network down, CORS, browser
 * cancelled, etc.) — distinct from a non-OK HTTP response. The global
 * error handler shows a "We can't reach the server" toast for this case.
 */
export class ApiNetworkError extends ApiError {
  constructor(message: string) {
    super(message, 0);
    this.name = 'ApiNetworkError';
  }
}

/**
 * Best-effort human-readable message from a FastAPI error `detail`. The detail
 * may be a string (HTTPException), an object (e.g. our 409 AlreadyFinalized
 * payload `{ message, status, submitted_at }`), or a list of validation errors
 * (422: `[{ loc, msg, type }, …]`). Returns `undefined` when nothing readable is
 * found so the caller can fall back to a generic message — never "[object Object]".
 */
export function messageFromDetail(detail: unknown): string | undefined {
  if (typeof detail === 'string') return detail || undefined;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) =>
        d && typeof d === 'object' && 'msg' in d
          ? String((d as { msg: unknown }).msg)
          : null,
      )
      .filter((m): m is string => Boolean(m));
    return msgs.length ? msgs.join(' · ') : undefined;
  }
  if (detail && typeof detail === 'object' && 'message' in detail) {
    const m = (detail as { message: unknown }).message;
    if (typeof m === 'string' && m) return m;
  }
  return undefined;
}

/**
 * The backend's app-wide duplicate-write 409 (backend/main.py `duplicate_write_handler`): a
 * double-click or two tabs collided on a unique rule. Its detail is labelled
 * `{ message, code: "duplicate_write" }` so a screen that stays quiet on its OWN structured
 * 409s can still show this one.
 */
export function isDuplicateWriteConflict(err: unknown): boolean {
  if (!(err instanceof ApiConflictError)) return false;
  const detail = (err.detail as { detail?: unknown } | null)?.detail;
  return (
    !!detail && typeof detail === 'object' && (detail as { code?: unknown }).code === 'duplicate_write'
  );
}

export interface ApiCallOptions extends Omit<RequestInit, 'body'> {
  /** JSON-serializable body. Use `bodyRaw` instead for FormData / Blob. */
  json?: unknown;
  /** Raw body (FormData, Blob, etc.). Pass this when sending multipart. */
  bodyRaw?: BodyInit;
  /** When true, treat 204 No Content as a resolved-null instead of erroring. */
  allow204?: boolean;
  /** When true, 401 responses throw `ApiError` instead of redirecting to
   *  `/login`. Use on the login + signup pages so the auth-status check
   *  doesn't trigger a redirect loop (we're already on /login). */
  skipAuthRedirect?: boolean;
  /** Abort the request after this many ms and throw ApiNetworkError. Use on long
   *  uploads so a request the proxy silently drops becomes a visible error
   *  instead of a page stuck on "Uploading…" (staging 2026-09-12). */
  timeoutMs?: number;
}

/** Body + response headers — for endpoints that carry metadata in headers
 *  (e.g. /api/products pagination: X-Next-Cursor / X-Total-Count). */
export interface ApiCallResult<T> {
  data: T;
  headers: Headers;
}

export async function apiCall<T>(path: string, opts: ApiCallOptions = {}): Promise<T> {
  return (await apiCallWithMeta<T>(path, opts)).data;
}

export async function apiCallWithMeta<T>(
  path: string,
  opts: ApiCallOptions = {},
): Promise<ApiCallResult<T>> {
  const { json, bodyRaw, allow204, skipAuthRedirect, headers, timeoutMs, ...rest } = opts;

  const finalHeaders: Record<string, string> = {
    ...(headers as Record<string, string> | undefined),
  };
  // Nobody has touched the page for a minute, so this is an auto-refresh, not the person:
  // it must not reset the admin session's 30-minute idle clock (see lib/user-activity.ts).
  if (path.startsWith('/api/admin/') && !userRecentlyActive()) {
    finalHeaders[ADMIN_BACKGROUND_HEADER] = '1';
  }
  let body: BodyInit | undefined;

  if (json !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
    body = JSON.stringify(json);
  } else if (bodyRaw !== undefined) {
    body = bodyRaw;
  }

  let res: Response;
  const controller = timeoutMs != null && rest.signal == null ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    res = await fetch(path, {
      credentials: 'same-origin',
      ...rest,
      ...(controller ? { signal: controller.signal } : {}),
      headers: finalHeaders,
      body,
    });
  } catch (err) {
    // fetch() throws on actual network failures (offline, DNS, CORS) —
    // distinct from a non-OK HTTP response. Surface as a typed network
    // error so the global handler can show "can't reach the server".
    if (controller?.signal.aborted) {
      throw new ApiNetworkError('The request took too long and was stopped — please try again.');
    }
    throw new ApiNetworkError(
      err instanceof Error ? err.message : 'Network request failed',
    );
  } finally {
    if (timer != null) clearTimeout(timer);
  }

  // 401 → redirect (preserve target in ?next=). Pages that handle anon
  // state themselves (login, signup) opt out via `skipAuthRedirect`.
  if (res.status === 401) {
    if (skipAuthRedirect) {
      throw new ApiError('Unauthorized', 401);
    }
    // The admin panel is a separate auth surface with its own login page and no
    // ?next= convention (see features/admin/api.ts) — a 401 there must never send
    // someone to the customer /login.
    if (window.location.pathname.startsWith('/admin')) {
      if (window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
      throw new ApiError('Unauthorized', 401);
    }
    // Already on the login page? Then there is nowhere to send anyone, and reloading would
    // only throw away the page they are looking at. This happens every time a request outlives
    // a sign-out: the browser lands on /login, the late reply arrives refused, and the whole
    // app reloads under them for no gain.
    if (window.location.pathname !== '/login') {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?next=${next}`;
    }
    throw new ApiError('Unauthorized', 401);
  }

  // 204 No Content → resolve null. Callers opt in via `allow204` (kept as an
  // explicit caller-intent marker + to keep it out of the fetch `...rest`); a
  // 204 resolves to null regardless.
  if (res.status === 204) {
    void allow204;
    return { data: null as T, headers: res.headers };
  }

  // 409 conflict → typed error
  if (res.status === 409) {
    const body = await res.json().catch(() => ({}));
    const rawDetail = (body as { detail?: unknown } | null)?.detail;
    const message = messageFromDetail(rawDetail) ?? 'Conflict';
    throw new ApiConflictError(message, body);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const rawDetail = (body as { detail?: unknown } | null)?.detail;
    // Durable access suspension → a super-admin has blocked this account/user.
    // Terminal state (re-login just hits the same block), so route to the
    // dedicated screen rather than surfacing a raw error or /login loop.
    if (res.status === 403 && rawDetail === 'access_suspended') {
      if (window.location.pathname !== '/suspended') {
        window.location.href = '/suspended';
      }
      throw new ApiError('access_suspended', 403);
    }
    // Billing block → an invoice went unpaid. Deliberately NOT routed to
    // /suspended: that screen is a dead end, and this one is fixable by the
    // tenant. /billing is where they add a payment method and unblock
    // themselves, which is the whole reason the gate sits at dispatch rather
    // than at authentication.
    //
    // Everyone is routed there, including a teammate who may not see billing
    // (spec D.1) — /billing renders "ask your account owner" for them, which
    // is more use than a raw toast. This module cannot read the current user
    // (importing the query client would be circular), so the page owns that
    // distinction, not this branch.
    if (res.status === 403 && rawDetail === 'billing_unpaid') {
      if (window.location.pathname !== '/billing') {
        window.location.href = '/billing';
      }
      throw new ApiError('billing_unpaid', 403);
    }
    const message = messageFromDetail(rawDetail) ?? `HTTP ${res.status}`;
    if (res.status >= 500) {
      throw new ApiServerError(message, res.status);
    }
    throw new ApiError(message, res.status, rawDetail);
  }

  // 200/201 — parse JSON body
  return { data: (await res.json()) as T, headers: res.headers };
}
